import { z } from 'zod'
import { exclusionViewSchema } from '@/features/compliance/exclusionAccess'
import { api } from '@/api/client'
import type { LoginInput } from '@/features/auth/schemas/authSchemas'
import {
  developmentSession,
  type DevelopmentSession,
} from '@/features/compliance/development/developmentSession'
import type {
  Customer,
  CustomerDetail,
  CustomerPage,
  CustomerQuery,
} from '@/features/customers/types'
import type { CreateCustomerInput } from '@/features/customers/schemas/customerSchema'
import type {
  BookAppointmentInput,
  GrantCreditInput,
} from '@/features/actions/schemas/actionSchemas'
import { toOffsetDateTime } from '@/features/scheduler/schedulerDates'
import {
  showRecordSchema,
  type ShowAction,
  type ShowContext,
  type Questionnaire,
} from '@/features/compliance/demo/showModeModel'
import { visitSchema, type eligibility } from '@/features/compliance/demo/showVisitModel'
import { useAuthStore } from '@/stores/authStore'
import type { Credits } from '@/types/models'
import type { CreditGrant } from '@/api/customersApi'

const balanceSchema = z.object({
  purchased: z.number().int().nonnegative(),
  consumed: z.number().int().nonnegative(),
  reserved: z.number().int().nonnegative(),
  available: z.number().int().nonnegative(),
  openContingent: z.number().int().nonnegative(),
})
const customerSchema = z.object({
  id: z.string(),
  name: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string(),
  createdAt: z.string(),
  stage: z.string(),
  stageLabel: z.string(),
  stageSection: z.string(),
  origin: z.string(),
  originLabel: z.string(),
  purchased: z.number(),
  remaining: z.number(),
  credits: balanceSchema,
  mobile: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  language: z.string().optional(),
  bodyType: z.string().optional(),
  programPlan: z.string().optional(),
})
const workspaceSchema = z.object({
  revision: z.number().int().nonnegative(),
  locationId: z.string(),
  timezone: z.string(),
  validityMonths: z.number().int().min(1).max(36),
  customers: z.array(customerSchema),
  appointments: z.array(visitSchema),
  devices: z.array(z.object({ id: z.string(), name: z.string(), position: z.number() })),
})
type Workspace = z.infer<typeof workspaceSchema>

function session(location?: string) {
  const current = useAuthStore.getState()
  if (
    current.status !== 'authenticated' ||
    !current.session?.location ||
    (location && current.session.location.publicId !== location)
  )
    throw new Error('Die Sitzung hat sich geändert. Bitte öffnen Sie die Ansicht erneut.')
  return { ...current.session, location: current.session.location }
}
function root(location = session().location.publicId) {
  return `/show/locations/${encodeURIComponent(location)}`
}
function recordPath(context: ShowContext) {
  session(context.locationId)
  return `${root(context.locationId)}/customers/${encodeURIComponent(context.customerId)}/compliance`
}
async function workspace(): Promise<Workspace> {
  const before = session()
  const value = workspaceSchema.parse(await api.get(`${root(before.location.publicId)}/workspace`))
  session(before.location.publicId)
  if (value.locationId !== before.location.publicId)
    throw new Error('Der Standort der Antwort stimmt nicht überein.')
  return value
}
function pipeline(customer: Customer) {
  return {
    stage: customer.stage!,
    stageLabel: customer.stageLabel!,
    stageSection: customer.stageSection!,
    attemptCount: 0,
    attemptsToday: 0,
    blockedForToday: false,
    restPhaseUntil: null,
    salesProcessClosed: false,
    manualOverride: false,
    nextActionAt: null,
    dueToday: false,
  }
}
async function command(body: Record<string, unknown>, revision?: number) {
  const before = session()
  const expectedRevision = revision ?? (await workspace()).revision
  session(before.location.publicId)
  const value = workspaceSchema.parse(
    await api.post(`${root(before.location.publicId)}/workspace/commands`, {
      requestId: crypto.randomUUID(),
      expectedRevision,
      ...body,
    }),
  )
  session(before.location.publicId)
  if (value.locationId !== before.location.publicId)
    throw new Error('Der Standort der Antwort stimmt nicht überein.')
  return value
}
function person(ws: Workspace, customerId: string) {
  const value = ws.customers.find((c) => c.id === customerId)
  if (!value) throw new Error('Der Kunde wurde nicht gefunden.')
  return value
}
function detail(ws: Workspace, customerId: string): CustomerDetail {
  const value = person(ws, customerId)
  return {
    ...value,
    company: session(ws.locationId).location.name,
    pipeline: pipeline(value),
    recentCalls: [],
    appointments: ws.appointments
      .filter((a) => a.customerId === customerId)
      .map((a) => ({
        id: a.id,
        deviceId: a.deviceId,
        deviceName: a.deviceName,
        startsAt: toOffsetDateTime(a.date, a.start, ws.timezone),
        endsAt: toOffsetDateTime(a.date, a.end, ws.timezone),
        kind: a.kind,
        kindLabel: a.kindLabel ?? a.kind,
        status: a.status,
        statusLabel: a.statusLabel ?? a.status,
        creditState: a.creditState,
        creditStateLabel: a.creditStateLabel,
      })),
  }
}
async function medical(
  context: ShowContext,
  revision: number,
  action: Record<string, unknown>,
  signal?: AbortSignal,
) {
  const value = showRecordSchema.parse(
    await api.post(
      `${recordPath(context)}/actions`,
      { requestId: crypto.randomUUID(), expectedRevision: revision, ...action },
      { signal },
    ),
  )
  session(context.locationId)
  if (
    value.scope.customerId !== context.customerId ||
    value.scope.locationId !== context.locationId
  )
    throw new Error('Der Aktenkontext stimmt nicht überein.')
  return value
}

/** No local fallback: every shared-show command is acknowledged by the authenticated server. */
export const sharedShowApi = {
  async exclusions(context: ShowContext) {
    const value = exclusionViewSchema.parse(await api.get(`${recordPath(context)}/exclusions`))
    session(context.locationId)
    if (
      value.scope.customerId !== context.customerId ||
      value.scope.locationId !== context.locationId
    )
      throw new Error('Der Aktenkontext stimmt nicht überein.')
    return value
  },
  async login(input: LoginInput) {
    await api.get('/show/csrf')
    const value = await api.post<DevelopmentSession>(
      '/show/session',
      new URLSearchParams({ username: input.email, password: input.password }),
    )
    await api.get('/show/csrf')
    return developmentSession(value)
  },
  async session() {
    const value = await api.get<DevelopmentSession>('/show/session')
    await api.get('/show/csrf')
    return developmentSession(value)
  },
  async switchLocation(location: string) {
    return developmentSession(
      await api.put<DevelopmentSession>('/show/session/location', { location }),
    )
  },
  async logout() {
    await api.post('/show/logout')
    await api.get('/show/csrf')
  },
  workspace,
  async appointments() {
    return workspace()
  },
  async fetchPage(query: CustomerQuery): Promise<CustomerPage> {
    const data = await workspace(),
      search = query.q?.trim().toLocaleLowerCase('de') ?? ''
    const all = data.customers
      .filter(
        (c) =>
          (!query.stage || c.stage === query.stage) &&
          (!search || `${c.name} ${c.email} ${c.phone}`.toLocaleLowerCase('de').includes(search)),
      )
      .sort((a, b) =>
        `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'de'),
      )
    const page = query.page ?? 0,
      size = query.size ?? 50
    return { items: all.slice(page * size, (page + 1) * size), total: all.length, page, size }
  },
  async fetchDetail(id: string) {
    return detail(await workspace(), id)
  },
  async createCustomer(
    input: Pick<CreateCustomerInput, 'name' | 'email' | 'phone'> & Partial<CreateCustomerInput>,
    origin = 'website',
  ) {
    const before = await workspace()
    const next = await command(
      {
        type: 'customer',
        name: input.name,
        email: input.email,
        phone: input.phone,
        origin,
        profile: Object.fromEntries(
          ['mobile', 'city', 'country', 'language', 'bodyType', 'programPlan']
            .filter((key) => key in input)
            .map((key) => [key, input[key as keyof typeof input]]),
        ),
      },
      before.revision,
    )
    const created = next.customers.find((c) => !before.customers.some((old) => old.id === c.id))
    if (!created)
      throw new Error(
        'Der neue Kunde konnte nicht zugeordnet werden. Bitte laden Sie die Liste neu.',
      )
    return detail(next, created.id)
  },
  async updateCustomer(id: string, input: CreateCustomerInput) {
    const before = await workspace()
    return detail(
      await command(
        {
          type: 'update-customer',
          customerId: id,
          name: input.name,
          email: input.email,
          phone: input.phone,
          origin: person(before, id).origin,
          profile: {
            mobile: input.mobile,
            city: input.city,
            country: input.country,
            language: input.language,
            bodyType: input.bodyType,
            programPlan: input.programPlan,
          },
        },
        before.revision,
      ),
      id,
    )
  },
  async book(input: BookAppointmentInput) {
    const before = await workspace()
    const next = await command(
      {
        type: 'book',
        customerId: input.subjectId,
        deviceId: input.deviceId,
        date: input.date,
        start: input.start,
        durationMinutes: input.durationMinutes,
        kind: input.kind,
      },
      before.revision,
    )
    const appointment = next.appointments.find(
      (a) => !before.appointments.some((old) => old.id === a.id),
    )
    if (!appointment)
      throw new Error(
        'Der Termin konnte nicht zugeordnet werden. Bitte laden Sie den Kalender neu.',
      )
    return {
      appointment,
      credits: person(next, input.subjectId).credits,
      stageChanged: false,
      previousStage: null,
      pipeline: pipeline(person(next, input.subjectId)),
    }
  },
  async grant(id: string, input: GrantCreditInput): Promise<CreditGrant> {
    const next = await command({
      type: 'grant',
      customerId: id,
      quantity: input.quantity,
      note: input.note,
    })
    return {
      customerPublicId: id,
      granted: {
        ...input,
        grantedAt: new Date().toISOString(),
        byUserName: session().user.displayName,
      },
      credits: person(next, id).credits,
      appointmentsChanged: [],
    }
  },
  async read(context: ShowContext) {
    const result = showRecordSchema.parse(await api.get(recordPath(context)))
    session(context.locationId)
    if (
      result.scope.customerId !== context.customerId ||
      result.scope.locationId !== context.locationId
    )
      throw new Error('Der Aktenkontext stimmt nicht überein.')
    return result
  },
  async check(context: ShowContext, appointment: string): Promise<ReturnType<typeof eligibility>> {
    const result = await api.get<ReturnType<typeof eligibility>>(`${recordPath(context)}/check`, {
      params: { appointmentId: appointment },
    })
    session(context.locationId)
    return result
  },
  act(
    context: ShowContext,
    revision: number,
    action: Exclude<ShowAction, { type: 'sign' }>,
    signal?: AbortSignal,
  ) {
    return medical(context, revision, action, signal)
  },
  async start(
    context: ShowContext,
    appointmentId: string,
    recordRevision: number,
    workspaceRevision: number,
    signal?: AbortSignal,
  ) {
    if (recordRevision !== workspaceRevision)
      throw new Error('Die Daten wurden inzwischen geändert. Bitte prüfen Sie erneut.')
    const record = await medical(
      context,
      workspaceRevision,
      { type: 'start', appointmentId },
      signal,
    )
    const treatment = record.treatments.find((t) => t.appointmentId === appointmentId)
    if (!treatment)
      throw new Error('Der Check-in konnte nicht bestätigt werden. Bitte laden Sie die Akte neu.')
    return {
      appointmentId,
      status: 'checked_in',
      statusLabel: 'Eingecheckt',
      checkedInAt: treatment.startedAt,
      checkedInBy: treatment.actor.name,
      creditState: 'consumed',
      creditStateLabel: 'Verbraucht',
      credits: null as Credits | null,
    }
  },
  async sign(
    context: ShowContext,
    revision: number,
    input: Questionnaire,
    signature: string,
    confirmed: boolean,
    signal: AbortSignal,
    reviewed: { id: string; name: string },
    catalogVersionId = '2',
  ) {
    if (reviewed.id !== context.customerId) throw new Error('Der Kunde hat sich geändert.')
    const value = showRecordSchema.parse(
      await api.post(
        `${recordPath(context)}/sign`,
        {
          requestId: crypto.randomUUID(),
          expectedRevision: revision,
          catalogVersionId,
          reviewedCustomerName: reviewed.name,
          confirmed,
          ...input,
          signature,
        },
        { signal },
      ),
    )
    session(context.locationId)
    if (
      value.scope.customerId !== context.customerId ||
      value.scope.locationId !== context.locationId
    )
      throw new Error('Der Aktenkontext stimmt nicht überein.')
    return value
  },
  async settings() {
    const state = await workspace()
    return { months: state.validityMonths, revision: state.revision }
  },
  async setValidity(months: number, revision: number) {
    await command({ type: 'settings', validityMonths: months }, revision)
  },
}
